import { cadenciaHasUnsavedChanges, snapshotForSave, watchCadenciaSessionClose } from './cadencia-save';
import { useEffect, useRef, useState } from 'react';
import type { DocumentSession } from '../core/session';
import { serializeProject } from '../persistence/project-format';
import { Canvas2DRenderer } from '../render/renderer';

type SaveSnapshot = { type: 'maipo:save'; requestId: string; source: Blob; preview: Blob; revision: number };
type Phase = 'ready' | 'editing' | 'saving' | 'saved' | 'error';

// A window-bound protocol: no arbitrary network URLs or credentials enter the editor.
export function CadenciaBridge({ open, session }: {
  open: (file: File) => Promise<DocumentSession>;
  session: DocumentSession;
}) {
  const [status, setStatus] = useState('Connecting to Cadencia…');
  const [phase, setPhase] = useState<Phase>('ready');
  const [busy, setBusy] = useState(false);
  const [savedRevision, setSavedRevision] = useState<number | null>(null);
  const [boundSession, setBoundSession] = useState<DocumentSession | null>(null);
  const [closed, setClosed] = useState(false);
  const closedDirty = useRef(false);
  const savedPoint = useRef<number | null>(null);
  const [, refresh] = useState(0);
  const bound = useRef<DocumentSession | null>(null);
  const saving = useRef(false);
  const activeSession = useRef(session); activeSession.current = session;
  const received = useRef(false);
  const pending = useRef<SaveSnapshot | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const saveAction = useRef<() => Promise<void>>();
  useEffect(() => () => clearTimeout(timeout.current), []);
  const params = new URLSearchParams(window.location.hash.slice(1));
  const origin = params.get('cadencia');
  const token = params.get('token');
  const opener = window.parent !== window ? window.parent : window.opener as Window | null;
  const enabled = Boolean(origin && /^https?:\/\/[^/]+$/.test(origin) && token && opener);
  const revision = boundSession?.currentRevision ?? 0;
  const liveEdit = boundSession?.history.hasLiveCommand ?? false;
  const dirty = closed ? closedDirty.current : savedRevision !== null && cadenciaHasUnsavedChanges(revision, savedRevision, liveEdit);
  const canSave = Boolean(boundSession && !closed && !boundSession.closedSignal.aborted && session === boundSession && !liveEdit && !busy);
  const message = closed ? 'Connected artwork closed. Reopen its saved source in Cadencia, or restore your local Maipo copy.'
    : session !== boundSession && boundSession ? 'Return to the connected artwork to save it.'
    : phase === 'error' ? status
    : busy ? 'Saving draft to Cadencia…'
    : liveEdit ? 'Finish the current edit to save'
    : dirty ? 'Unsaved changes · save a draft when ready'
    : phase === 'saved' ? 'Draft saved in Cadencia'
    : status;

  useEffect(() => {
    if (!boundSession) return;
    return boundSession.subscribe(() => refresh(value => value + 1));
  }, [boundSession]);
  useEffect(() => {
    if (!boundSession) return;
    return watchCadenciaSessionClose(boundSession, () => savedPoint.current ?? boundSession.currentRevision, unsaved => {
      closedDirty.current = unsaved;
      setClosed(true); setPhase('error');
    });
  }, [boundSession]);
  useEffect(() => {
    if (!enabled || !boundSession || savedRevision === null) return;
    const publish = () => opener!.postMessage({
      type: 'maipo:state', version: 1, token,
      dirty: closed ? closedDirty.current : cadenciaHasUnsavedChanges(boundSession.currentRevision, savedRevision, boundSession.history.hasLiveCommand),
      saving: busy, phase: closed ? 'error' : busy ? 'saving' : phase === 'error' ? 'error' : dirty ? 'editing' : phase,
      revision: boundSession.currentRevision, savedRevision, canSave,
      hasPendingSave: Boolean(pending.current), message,
    }, origin!);
    publish();
    // Reconcile after a parent listener reconnects without reloading the artwork.
    const timer = window.setInterval(publish, 5000);
    return () => clearInterval(timer);
  }, [enabled, origin, token, opener, boundSession, revision, liveEdit, savedRevision, busy, phase, dirty, canSave, message, closed]);

  useEffect(() => {
    if (!enabled) return;
    const send = (data: object) => opener!.postMessage({ ...data, token }, origin!);
    const receive = async (event: MessageEvent<unknown>) => {
      if (!event.data || typeof event.data !== 'object') return;
      const data = event.data as Record<string, unknown>;
      if (event.origin !== origin || event.source !== opener || data.token !== token) return;
      if (data.type === 'cadencia:open' && !received.current && data.file instanceof Blob && typeof data.name === 'string') {
        received.current = true;
        try {
          const opened = await open(new File([data.file], data.name));
          bound.current = opened;
          savedPoint.current = opened.currentRevision;
          setSavedRevision(opened.currentRevision);
          setBoundSession(opened);
          setPhase('ready');
          setStatus('Ready to edit · saves create draft versions');
          send({ type: 'maipo:opened', protocolVersion: 1 });
        } catch (error) {
          setPhase('error'); setStatus(String(error));
          send({ type: 'maipo:error', message: String(error) });
        }
      }
      if (data.type === 'cadencia:save') await saveAction.current?.();
      const awaiting = pending.current;
      if (data.type === 'cadencia:saved' && awaiting && data.requestId === awaiting.requestId) {
        clearTimeout(timeout.current);
        const saved = awaiting.revision;
        bound.current?.markSaved(saved);
        savedPoint.current = saved;
        setSavedRevision(saved);
        pending.current = null;
        saving.current = false; setBusy(false); setPhase('saved');
        setStatus('Draft saved in Cadencia');
      }
      if (data.type === 'cadencia:error' && awaiting && data.requestId === awaiting.requestId) {
        clearTimeout(timeout.current);
        saving.current = false; setBusy(false); setPhase('error');
        setStatus(typeof data.message === 'string' ? data.message : 'Save failed. Retry to keep your draft.');
      }
    };
    const listener = (event: MessageEvent<unknown>) => { void receive(event); };
    window.addEventListener('message', listener);
    const ready = () => { if (!received.current) send({ type: 'maipo:ready' }); };
    ready();
    const timer = window.setInterval(ready, 1000);
    return () => { clearInterval(timer); window.removeEventListener('message', listener); };
  }, [enabled, origin, token, open, opener]);

  const save = async () => {
    const current = bound.current;
    if (!current || current.closedSignal.aborted || activeSession.current !== current || saving.current) return;
    saving.current = true; setBusy(true); setPhase('saving');
    try {
      const binding = current.captureRevision();
      pending.current = await snapshotForSave(pending.current, binding.revision, async () => {
        // Both representations capture the committed revision before async work yields.
        const canvas = new Canvas2DRenderer().flatten(binding.document);
        const previewPromise = new Promise<Blob>((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('PNG export failed')), 'image/png'));
        const [bytes, preview] = await Promise.all([serializeProject(binding.document), previewPromise]);
        const source = new Blob([bytes], { type: 'application/octet-stream' });
        const requestId = Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('').replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5');
        return { type: 'maipo:save' as const, requestId, source, preview };
      });
      if (!opener || opener.closed) throw new Error('Cadencia tab closed. Save a local Maipo copy to keep your work.');
      opener.postMessage({ ...pending.current, token }, origin!);
      timeout.current = setTimeout(() => {
        saving.current = false; setBusy(false); setPhase('error');
        setStatus('Save not confirmed. Retry, or save a local Maipo copy.');
      }, 60000);
    } catch (error) { saving.current = false; setBusy(false); setPhase('error'); setStatus(String(error)); }
  };
  saveAction.current = save;
  if (!enabled) return null;
  return <div className="cadencia-bridge" role="status"><span>{message}</span><button type="button" disabled={!canSave} onClick={() => void save()}>{pending.current?.revision === revision && !busy ? 'Retry save to Cadencia' : 'Save to Cadencia'}</button></div>;
}
